import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/cn";

import { Card } from "./Card";
import { Skeleton } from "./Skeleton";

type Tone = "brand" | "info" | "warn" | "danger";

export interface StatCardProps {
  /** Small upper-case label: TOTAL PRODUCTS, LOW STOCK ITEMS. */
  label: string;
  /** The figure. `null` renders the empty dash — never a fabricated zero. */
  value: string | null;
  /** Supporting line: "All active products", "Requires immediate attention". */
  hint?: string | undefined;
  icon: LucideIcon;
  tone?: Tone | undefined;
  loading?: boolean | undefined;
  className?: string | undefined;
}

const TONES: Record<Tone, { chip: string; icon: string }> = {
  brand: { chip: "bg-brand-50", icon: "text-brand-500" },
  info: { chip: "bg-info-50", icon: "text-info-500" },
  warn: { chip: "bg-warn-50", icon: "text-warn-500" },
  danger: { chip: "bg-danger-50", icon: "text-danger-500" },
};

/**
 * The KPI card from the Inventory design: soft tinted circular icon on the left,
 * label, figure and hint stacked on the right.
 *
 * `value: null` renders an em dash rather than `0`. The distinction matters — a
 * merchant seeing "0 products" concludes their catalogue was wiped, whereas "—"
 * reads correctly as "not counted yet".
 */
export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "brand",
  loading = false,
  className,
}: StatCardProps) {
  const palette = TONES[tone];

  return (
    <Card className={cn("flex items-center gap-4", className)}>
      <span
        className={cn("grid size-12 shrink-0 place-items-center rounded-full", palette.chip)}
        aria-hidden
      >
        <Icon className={cn("size-6", palette.icon)} strokeWidth={2} />
      </span>

      <div className="min-w-0">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-500">
          {label}
        </p>

        {loading ? (
          <Skeleton className="mt-1.5 h-7 w-24" />
        ) : (
          <p className="tabular mt-0.5 text-kpi text-ink-900">
            {value ?? <span className="text-ink-400">—</span>}
          </p>
        )}

        {hint && <p className="mt-0.5 truncate text-xs text-ink-500">{hint}</p>}
      </div>
    </Card>
  );
}
